<div class="layout" [class.sidebar-open]="sidebarOpen()"
    [class.layout-horizontal]="layoutMode() === 'horizontal'"
    [class.layout-vertical]="layoutMode() === 'vertical'">
    <!-- ===================== SIDEBAR ===================== -->
    <aside class="sidebar">
        <div class="sidebar-brand">
            <img class="brand-logo" src="assets/arogyaLOGO.png" alt="Arogya Kavach Solutions" />
            <button type="button" class="sidebar-close d-lg-none" (click)="closeSidebar()" aria-label="Close menu">
                <i class="pi pi-times"></i>
            </button>
        </div>

        <!-- Searchable menu -->
        <div class="sidebar-search">
            <p-iconfield iconPosition="left">
                <p-inputicon styleClass="pi pi-search" />
                <input type="text" pInputText placeholder="Search menu..." [ngModel]="search()"
                    (ngModelChange)="search.set($event)">
            </p-iconfield>
        </div>

        <nav class="sidebar-nav">
            @if (menuLoading()) {
                <p class="nav-empty"><i class="pi pi-spin pi-spinner"></i> Loading menu...</p>
            } @else {
                <!-- recursive menu renderer -->
                <ng-template #menuNodes let-nodes let-depth="depth">
                    @for (node of nodes; track node.menuId) {
                        <div class="nav-node" [class.depth-0]="depth === 0"
                            [class.open]="isExpanded(node.menuId)">
                            <button type="button" class="nav-item" [class.active]="isActive(node)"
                                [class.has-children]="node.children?.length"
                                [style.opacity]="isLocked(node) ? '.55' : null"
                                [style.paddingLeft.px]="14 + depth * 16" (click)="onNode(node)"
                                (mouseenter)="onTopHover($event)">
                                <i [class]="iconOf(node)"></i>
                                <span class="nav-label">{{ node.title }}</span>
                                @if (node.children?.length) {
                                    <i class="caret pi"
                                        [class.pi-chevron-down]="isExpanded(node.menuId)"
                                        [class.pi-chevron-right]="!isExpanded(node.menuId)"></i>
                                } @else if (isLocked(node)) {
                                    <i class="caret pi pi-lock" title="KYC approval required"></i>
                                }
                            </button>
                            @if (node.children?.length) {
                                <div class="nav-children">
                                    <ng-container
                                        *ngTemplateOutlet="menuNodes; context: { $implicit: node.children, depth: depth + 1 }">
                                    </ng-container>
                                </div>
                            }
                        </div>
                    }
                </ng-template>

                @if (filteredMenu().length) {
                    <ng-container
                        *ngTemplateOutlet="menuNodes; context: { $implicit: filteredMenu(), depth: 0 }">
                    </ng-container>
                } @else {
                    <p class="nav-empty">No menu found</p>
                }
            }
        </nav>

        <div class="sidebar-footer">
            <button type="button" class="nav-item logout" (click)="logout()" [disabled]="loading()">
                <i class="pi pi-sign-out"></i>
                <span class="nav-label">{{ loading() ? 'Logging out...' : 'Logout' }}</span>
            </button>
            <div class="sidebar-powered">
                <span>Powered by</span>
                <span class="lapitech-chip">
                    <img src="assets/lapitech-logo.png?v=2" alt="Lapitech Software Solutions" />
                </span>
            </div>
        </div>
    </aside>

    <!-- Mobile backdrop -->
    <div class="sidebar-backdrop" (click)="closeSidebar()"></div>

    <!-- ===================== MAIN ===================== -->
    <div class="main">
        <!-- Test mode must never be silently on: anything enrolled now is test data and will be
             deleted by the next purge, so it is called out above everything else. -->
        @if (testMode.showingTest()) {
            <div class="testmode-bar">
                <span class="tm-pill">TEST DATA</span>
                @if (isAdmin) {
                    <span>Showing test records only — live business is hidden while this view is on.</span>
                } @else {
                    <!-- A partner cannot switch this: its account is in test mode, so everything it
                         onboards is a test record and only those records are shown. -->
                    <span>
                        {{ testMode.pinnedOwner() || 'This account' }} is in test mode — members
                        enrolled now are practice records and can be cleared by your administrator.
                    </span>
                }
                @if (isAdmin) {
                    <!-- Click handler, not routerLink: this layout does not import RouterLink,
                         so a routerLink here renders as an inert attribute and does nothing. -->
                    <button type="button" class="tm-link" (click)="goTestMode()">Manage test data</button>
                }
            </div>
        }

        <header class="topbar">
            <button type="button" class="hamburger d-lg-none" (click)="toggleSidebar()" aria-label="Open menu">
                <i class="pi pi-bars"></i>
            </button>
            <div class="topbar-title">
                <h1>{{ pageTitle() }}</h1>
                <p>Welcome back, {{ user?.username || 'User' }}</p>
            </div>

            <!-- Global search — Ctrl+K spotlight over the user's menu -->
            <div class="topbar-search">
                <app-global-search />
            </div>

            <!-- Live date & time, top-right corner next to the user button -->
            <div class="topbar-clock d-none d-md-flex">
                <span class="clock-time">{{ clockTime() }}</span>
                <span class="clock-date">{{ clockDate() }}</span>
            </div>

            <!-- Live / Test switcher — always-visible chrome, admins only -->
            @if (isAdmin) {
                <app-mode-switch class="d-none d-md-inline-flex" />
            }

            <!-- KYC alert bell (admins only) -->
            @if (isAdmin) {
                <app-admin-bell></app-admin-bell>
            }

            <!-- Live accent switcher (rewrites the primary ramp; no reload) -->
            <app-theme-picker />

            <!-- Right-side user button + dropdown -->
            <div class="topbar-user">
                <button type="button" class="user-btn" (click)="toggleUserMenu()">
                    <span class="avatar">{{ initial }}</span>
                    <span class="user-meta d-none d-sm-flex">
                        <span class="user-name">{{ user?.username || 'User' }}</span>
                        <span class="user-role">{{ user?.roles?.[0] || 'Member' }}</span>
                    </span>
                    <i class="pi pi-angle-down"></i>
                </button>

                @if (userMenuOpen()) {
                    <div class="user-backdrop" (click)="closeUserMenu()"></div>
                    <div class="user-menu">
                        <div class="user-menu-head">
                            <span class="avatar lg">{{ initial }}</span>
                            <div>
                                <strong>{{ user?.username || 'User' }}</strong>
                                <small>Partner Code: {{ user?.partnerCode || '—' }}</small>
                            </div>
                        </div>
                        <!-- Layout switch: vertical sidebar <-> horizontal top menu (desktop) -->
                        <button type="button" class="user-menu-item d-none d-lg-flex"
                            (click)="toggleLayoutMode(); closeUserMenu()">
                            <i class="pi" [class.pi-arrows-h]="layoutMode() === 'vertical'"
                                [class.pi-arrows-v]="layoutMode() === 'horizontal'"></i>
                            {{ layoutMode() === 'vertical' ? 'Horizontal Menu' : 'Vertical Menu' }}
                        </button>
                        <button type="button" class="user-menu-item" (click)="goChangePassword()">
                            <i class="pi pi-key"></i>
                            Change Password
                        </button>
                        <button type="button" class="user-menu-item logout" (click)="logout()"
                            [disabled]="loading()">
                            <i class="pi pi-sign-out"></i>
                            {{ loading() ? 'Logging out...' : 'Logout' }}
                        </button>
                    </div>
                }
            </div>
        </header>

        <!-- Routed page content renders here -->
        <main class="content">
            <router-outlet></router-outlet>
        </main>
    </div>
</div>

